import React from "react";
import { GeneratedDataItem } from "./types";
import TextComponent from "./TextComponent";
import PeopleComponent from "./PeopleComponent";
import Table from "./TableComponent";
import ChartComponent from "./ChartComponent";
import GraphComponent from "./GraphComponent";
import ImageComponent from "./ImageComponent";
import TextAndImageComponent from "./TextAndImageComponent";
import ImageSection from "./ImageSection";
// Helper to convert backend table object to TableData shape
function mapBackendTableToTableData(table: any): {
  columnHeaders: string[];
  rowHeaders: string[];
  rows: string[][];
} {
  if (!table) {
    console.warn("No table data provided to mapBackendTableToTableData");
    return { columnHeaders: [], rowHeaders: [], rows: [] };
  }

  //console.log('Transforming table data:', table);

  const columnHeaders: string[] = [];
  for (let i = 0; i <= 5; i++) {
    const key = `columnHeader${i}`;
    if (table[key] && table[key].trim() !== "") {
      columnHeaders.push(table[key]);
    }
  }

  const rowHeaders: string[] = [];
  const rows: string[][] = [];

  for (let i = 1; i <= 8; i++) {
    const rowHeaderKey = `rowHeader${i}`;
    const rowDataKey = `rows${i}`;

    // Only include rows that have either a header or some data
    const hasRowHeader =
      table[rowHeaderKey] && table[rowHeaderKey].trim() !== "";
    const rowData = table[rowDataKey];
    const hasRowData =
      rowData &&
      Object.values(rowData).some(
        (val: any) => val && val.toString().trim() !== ""
      );

    if (hasRowHeader && hasRowData) {
      rowHeaders.push(table[rowHeaderKey] || `Row ${i}`);

      const row: string[] = [];
      for (let j = 1; j <= 5; j++) {
        row.push((rowData && rowData[`attribute${j}`]) || "");
      }
      rows.push(row);
    }
  }

  // Don't add empty rows by default - only show what has data

  //console.log('Transformed table data:', { columnHeaders, rowHeaders, rows });

  return {
    columnHeaders,
    rowHeaders,
    rows,
  };
}

interface ContentRendererProps {
  item: GeneratedDataItem;
  editMode: Record<string, boolean>;
  editData: Record<string, any>;
  toggleEditMode: (id: string) => void;
  saveChanges: (id: string) => void;
  cancelEdit: (id: string) => void;
  handleInputChange: (
    id: string,
    field: string,
    value: any,
    componentType?: string
  ) => void;
  saving: Record<string, boolean>;
  editable: boolean;
}

const ContentRenderer: React.FC<ContentRendererProps> = ({
  item,
  editMode,
  editData,
  toggleEditMode,
  saveChanges,
  cancelEdit,
  handleInputChange,
  saving,
  editable,
}) => {
  // Get the appropriate title based on component type
  const title =
    item.text?.title ||
    item.people?.title ||
    item.table?.title ||
    item.chart?.title ||
    item.textandimage?.title ||
    item.graph?.title ||
    item.component_type ||
    "Untitled";

  // Listen for force view mode messages
  React.useEffect(() => {
    // Handle postMessage events
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.type === "forceViewMode" &&
        event.data.componentId === item._id
      ) {
        // Force this component into view mode when it gets the message
        if (editMode[item._id]) {
          toggleEditMode(item._id);
        }
      }
    };

    // Handle custom DOM events
    const handleCloseEditMode = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.componentId === item._id) {
        // Force this component into view mode when it gets the event
        if (editMode[item._id]) {
          toggleEditMode(item._id);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    window.addEventListener(
      "closeEditMode",
      handleCloseEditMode as EventListener
    );

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener(
        "closeEditMode",
        handleCloseEditMode as EventListener
      );
    };
  }, [item._id, toggleEditMode, editMode]);

  return (
    <div
      key={item._id || `item-${Math.random()}`}
      className={`data-item p-1 md:p-2 my-1 md:my-2 ${
        editMode[item._id || ""]
          ? "border rounded-md shadow-sm border-blue-400 bg-blue-50"
          : ""
      }`}
      style={{
        touchAction: "pan-y",
        WebkitOverflowScrolling: "touch"
      }}
    >
      {/* Render based on component type */}
      <div className="mb-1 md:mb-2 p-0 md:p-1 overflow-hidden">
        {item.component_type === "Text" && (
          <TextComponent
            item={item}
            editMode={editMode[item._id || ""]}
            editData={editData[item._id || ""]}
            handleInputChange={handleInputChange}
          />
        )}

        {item.component_type === "People" && (
          <PeopleComponent
            item={item}
            editMode={editMode[item._id || ""]}
            editData={editData[item._id || ""]}
            handleInputChange={handleInputChange}
          />
        )}

        {item.component_type === "Table" && (
          <Table
            item={item}
            editMode={editMode[item._id || ""]}
            editData={editData[item._id || ""]}
            handleInputChange={handleInputChange}
            toggleEditMode={toggleEditMode}
            saveChanges={saveChanges}
            cancelEdit={cancelEdit}
            saving={saving}
            heading={item.table?.title || "Table"}
            slideType={item.component_type || "Table"}
            documentID={item.documentID}
            orgId={item.documentID?.split("-")[1] || "defaultOrg"}
            authToken={sessionStorage.getItem("authToken") || ""}
            setDisplayMode={() => {}}
            outlineID={item.outlineID}
            setIsSlideLoading={() => {}}
            setFailed={() => {}}
            // Provide direct rows/headers as a fallback for non-item consumers
            rows={mapBackendTableToTableData(item.table).rows}
            columnHeaders={mapBackendTableToTableData(item.table).columnHeaders}
            rowHeaders={mapBackendTableToTableData(item.table).rowHeaders}
          />
        )}

        {item.component_type === "Chart" && (
          <ChartComponent
            chartData={item.chart || item}
            title={item.chart?.title || item.component_type}
            height={350}
            editMode={editMode[item._id || ""]}
            editData={editData[item._id || ""]}
            handleInputChange={handleInputChange}
            toggleEditMode={toggleEditMode}
            saveChanges={saveChanges}
            cancelEdit={cancelEdit}
            saving={saving}
            item={item}
          />
        )}

        {item.component_type === "Image" && (
          <ImageComponent
            item={item}
            editMode={editMode[item._id || ""]}
            editData={editData[item._id || ""]}
            handleInputChange={handleInputChange}
          />
        )}

        {item.component_type === "TextandImage" && (
          <TextAndImageComponent
            item={item}
            editMode={editMode[item._id || ""]}
            editData={editData[item._id || ""]}
            handleInputChange={handleInputChange}
            saveChanges={saveChanges}
            cancelEdit={cancelEdit}
            // SectionRenderer will set a hideImage flag when refine panel is open
            hideImage={(item as any).__hideImage === true}
          />
        )}

        <div className="flex justify-between items-center mb-2">
          {/* <h3 className="text-xl font-bold">{title}</h3> */}
          {(item.component_type === "TextandImage" ||
            "Text" ||
            "Image" ||
            "Table") &&
            editable &&
            item._id &&
            editMode[item._id] && (
              <div className="flex flex-row gap-2 mt-5">
                <button
                  onClick={() => {
                    saveChanges(item._id);
                    editMode[item._id] = false;
                  }}
                  disabled={saving[item._id]}
                  className={`px-3 py-2 rounded text-white min-w-[80px] ${
                    saving[item._id]
                      ? "bg-gray-400"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {saving[item._id] ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={() => cancelEdit(item._id)}
                  className="px-3 py-2 rounded text-white bg-gray-600 hover:bg-gray-700 min-w-[80px]"
                >
                  Cancel
                </button>
              </div>
            )}
        </div>

        {/* Additional component types can be added here */}
      </div>
    </div>
  );
};

export default ContentRenderer;
